<script setup lang="ts">
import FinestCountBadge from '@/components/FinestCountBadge.vue'
import type { OrgGraphNode } from '@/components/companies/orgGraph'
import { initials } from '@/utils/initials'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  data: OrgGraphNode
  interactive: boolean
  isMe: boolean
  isSubject: boolean
}>()

const emit = defineEmits<{
  edit: [id: string]
  delete: [id: string]
  deletePerson: [id: string]
}>()

const { t } = useI18n({ useScope: 'local' })

const isCompany = computed(() => props.data.type === 'company')

const isEditable = computed(
  () => isCompany.value && props.data.objectCount !== null && props.data.objectCount !== undefined,
)

const isDeletablePerson = computed(() => !isCompany.value && props.data.deletable === true)

const frameClass = computed(() => {
  if (props.isMe) {
    return 'node-me shadow-md'
  }

  return props.isSubject ? 'border-action bg-surface border' : 'border-line bg-surface border hover:border-[#c6dbfc]'
})
</script>

<template>
  <div
    class="group pointer-events-auto relative flex w-[246px] items-center gap-3 rounded-[10px] py-3 pr-3.5 pl-4 transition-all"
    :class="[interactive ? 'hover:-translate-y-0.5 hover:shadow-md' : '', frameClass]">
    <span
      v-if="!isMe"
      class="absolute top-2.5 bottom-2.5 left-0 w-[3px] rounded-full"
      :class="isCompany ? 'bg-action' : 'bg-brand-teal'" />

    <span
      v-if="isMe"
      class="chip-me absolute -top-2.5 left-3.5 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider text-white uppercase">
      {{ t('you') }}
    </span>

    <div
      class="flex size-10 flex-none items-center justify-center text-xs font-bold"
      :class="[
        isCompany ? 'text-brand-blue-1 bg-info-subtle rounded-md' : 'rounded-full text-white',
        !isCompany && isMe ? 'avatar-me' : '',
        !isCompany && !isMe ? 'avatar-person' : '',
      ]">
      <i v-if="isCompany" class="pi pi-building text-base" aria-hidden="true" />
      <i v-else-if="isMe" class="pi pi-user text-base" aria-hidden="true" />
      <span v-else>{{ initials(data.name, '?') }}</span>
    </div>

    <div class="min-w-0 flex-1">
      <div class="text-ink line-clamp-2 text-sm leading-tight font-bold">{{ data.name }}</div>
      <div v-if="!isCompany || isEditable" class="text-ink-3 mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
        <span
          v-if="isEditable"
          class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium"
          :class="(data.objectCount ?? 0) === 0 ? 'bg-surface-3 text-ink-3' : 'text-brand-blue-1 bg-info-subtle'">
          <i class="pi pi-box text-[10px]" aria-hidden="true" />
          {{ t('objects', { n: data.objectCount ?? 0 }, data.objectCount ?? 0) }}
        </span>
        <span v-else>{{ t('person') }}</span>
      </div>
    </div>

    <FinestCountBadge
      :count="data.missingCount ?? 0"
      :aria-label="t('missing', { n: data.missingCount ?? 0 })"
      class="flex-none" />

    <div
      v-if="interactive && (isEditable || isDeletablePerson)"
      class="nodrag nopan border-line bg-surface absolute -top-3.5 right-2 z-10 hidden gap-1 rounded-full border p-[3px] shadow-md group-hover:flex">
      <button
        v-if="isEditable"
        type="button"
        class="text-ink-3 hover:bg-info-subtle hover:text-action flex size-6 items-center justify-center rounded-full transition-colors"
        :title="t('edit')"
        :aria-label="t('edit')"
        @click.stop="emit('edit', data.id)">
        <i class="pi pi-pencil text-xs" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="text-ink-3 hover:bg-danger-subtle hover:text-danger flex size-6 items-center justify-center rounded-full transition-colors"
        :title="t('delete')"
        :aria-label="t('delete')"
        @click.stop="isEditable ? emit('delete', data.id) : emit('deletePerson', data.id)">
        <i class="pi pi-trash text-xs" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.node-me {
  border: 2px solid transparent;
  background:
    linear-gradient(var(--color-surface), var(--color-surface)) padding-box,
    var(--gradient-brand) border-box;
}

.avatar-person {
  background: linear-gradient(135deg, var(--color-brand-teal), var(--color-brand-cyan));
}

.avatar-me {
  background: var(--gradient-brand-soft);
}

.chip-me {
  background: var(--gradient-brand-soft);
}
</style>

<i18n lang="json">
{
  "de": {
    "objects": "keine Objekte | {n} Objekt | {n} Objekte",
    "person": "Person",
    "you": "Du",
    "missing": "{n} Angaben fehlen",
    "edit": "Bearbeiten",
    "delete": "Löschen"
  },
  "en": {
    "objects": "no properties | {n} property | {n} properties",
    "person": "Person",
    "you": "You",
    "missing": "{n} details missing",
    "edit": "Edit",
    "delete": "Delete"
  }
}
</i18n>
